import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { profileAPI } from '../../utils/api';
import useUserStore from '../../store/userStore';
import { ChevronRight, ChevronLeft, Check, User, Ruler, Activity, Dumbbell } from 'lucide-react';
import { toast } from 'sonner';
import useKeyboardAware from '../../hooks/useKeyboardAware';
import { isManualGoalMode, quizNextRoute } from './onboardingFlowLogic';
import { buildOnboardingProfileData, runAutomaticMacroSetup } from '../../utils/onboardingMacros';
import { restoreMacroQuizState } from './onboardingRestore';

// =========================================
// FITXESS Source-of-Truth Macro Calculator
// Version 3.0 - Must match backend exactly
// =========================================

/**
 * Calculate workout score from selected workouts.
 * Used for activity tier bumping.
 */
function calculateWorkoutScore(workouts) {
  if (!workouts || workouts.length === 0) return 0;
  
  const scores = {
    'strength': 1, 'strength_training': 1, 'weight_lifting': 1, 'weights': 1,
    'cardio': 1, 'running': 1, 'cycling': 1,
    'fitness_classes': 1, 'classes': 1, 'group_fitness': 1,
    'sports': 1, 'team_sports': 1,
    'hiit': 2, 'high_intensity': 2,
    'mobility': 0, 'stretching': 0, 'yoga': 0, 'flexibility': 0,
    'other': 1
  };
  
  let total = 0;
  workouts.forEach(function(workout) {
    const key = workout.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
    let matched = false;
    Object.keys(scores).forEach(function(scoreKey) {
      if (key.includes(scoreKey) || scoreKey.includes(key)) {
        total += scores[scoreKey];
        matched = true;
      }
    });
    if (!matched) total += 1;
  });
  
  return total;
}

/**
 * Get final activity level after workout-based rules.
 * 
 * Rules:
 * 1. If NO workouts (score=0): cap at "light" to prevent inflated TDEE
 *    - sedentary stays sedentary
 *    - anything else becomes "light"
 * 2. If workouts exist (score>=1): bump up ONE tier max from baseline
 */
function getFinalActivityLevel(baseline, workoutScore) {
  const tierOrder = ['sedentary', 'light', 'moderate', 'active', 'very_active'];
  
  if (workoutScore === 0) {
    // No workouts = cap at "light" to prevent TDEE inflation
    if (baseline === 'sedentary') {
      return 'sedentary';
    }
    return 'light';  // Cap at light regardless of what they selected
  }
  
  // Workouts exist - bump up one tier from baseline
  const currentIdx = tierOrder.indexOf(baseline);
  const newIdx = Math.min(currentIdx >= 0 ? currentIdx + 1 : 2, tierOrder.length - 1);
  
  return tierOrder[newIdx];
}

/**
 * Get goal calorie adjustment.
 * Applied AFTER TDEE calculation.
 */
function getGoalAdjustment(goal, baselineActivity, workoutScore) {
  // Map "Lose Weight" to "Lose Body Fat" for consistency
  const normalizedGoal = goal === 'Lose Weight' ? 'Lose Body Fat' : goal;
  
  // Special rule for very active trainees
  if (normalizedGoal === 'Lose Body Fat') {
    if (['active', 'very_active'].includes(baselineActivity) && workoutScore >= 3) {
      return -300; // Smaller deficit to prevent starving
    }
    return -375; // Standard deficit
  }
  
  const adjustments = {
    'Improve Health': 0,
    'Maintain': 0,
    'Body Recomposition': -150,
    'Build Muscle': 200,
    'Gain Weight': 350
  };
  
  return adjustments[normalizedGoal] || 0;
}

/**
 * Calculate protein in grams.
 */
function calculateProtein(weightLbs, goal, goalWeightLbs, sex) {
  const normalizedGoal = goal === 'Lose Weight' ? 'Lose Body Fat' : goal;
  
  let proteinG;
  
  if (['Lose Body Fat', 'Body Recomposition'].includes(normalizedGoal)) {
    if (goalWeightLbs && goalWeightLbs > 0) {
      proteinG = Math.round(goalWeightLbs * 0.90);
    } else {
      proteinG = Math.round(weightLbs * 0.85);
    }
  } else if (['Maintain', 'Improve Health'].includes(normalizedGoal)) {
    proteinG = Math.round(weightLbs * 0.75);
  } else if (['Build Muscle', 'Gain Weight'].includes(normalizedGoal)) {
    proteinG = Math.round(weightLbs * 0.80);
  } else {
    proteinG = Math.round(weightLbs * 0.75);
  }
  
  // Apply caps
  if (sex === 'female') {
    proteinG = Math.min(proteinG, 170);
  } else {
    proteinG = Math.min(proteinG, 220);
  }
  
  return proteinG;
}

/**
 * FITXESS Source-of-Truth Macro Calculation
 * 
 * CRITICAL ORDERING:
 * 1. Calculate BMR
 * 2. Bump activity tier from workouts (max 1 tier)
 * 3. Calculate TDEE = BMR * PAL
 * 4. Apply goal adjustment to TDEE (NOT to BMR)
 * 5. Derive macros from target_calories
 */
function calculateMacros(profileData) {
  // ========================================
  // STEP 0: Extract inputs
  // ========================================
  const weightKg = profileData.weight_kg || 70;
  const heightCm = profileData.height_cm || 170;
  const age = profileData.age || 30;
  const sex = (profileData.sex || 'male').toLowerCase();
  const baselineActivity = profileData.activity_level || 'moderate';
  const workouts = profileData.workout_types || profileData.workouts_selected || [];
  const goal = profileData.goal || 'Maintain';
  const goalWeightLbs = profileData.goal_weight_lbs;
  
  const weightLbs = weightKg * 2.20462;
  
  // ========================================
  // STEP 1: Calculate BMR (Mifflin-St Jeor)
  // ========================================
  let bmr = 10 * weightKg + 6.25 * heightCm - 5 * age;
  if (sex === 'male') {
    bmr += 5;
  } else if (sex === 'female') {
    bmr -= 161;
  } else {
    bmr -= 80; // Neutral for "other"
  }
  
  // ========================================
  // STEP 2: Workout score & activity tier bump
  // ========================================
  const workoutScore = calculateWorkoutScore(workouts);
  const finalActivity = getFinalActivityLevel(baselineActivity, workoutScore);
  
  // ========================================
  // STEP 3: Calculate TDEE (Maintenance)
  // CRITICAL: PAL applied here, BEFORE goal adjustment
  // ========================================
  const palMultipliers = {
    'sedentary': 1.20,
    'light': 1.375,
    'moderate': 1.55,
    'active': 1.725,
    'very_active': 1.90
  };
  const finalPal = palMultipliers[finalActivity] || 1.55;
  const tdee = bmr * finalPal;
  
  // ========================================
  // STEP 4: Goal adjustment (applied AFTER TDEE)
  // ========================================
  const goalAdjustment = getGoalAdjustment(goal, baselineActivity, workoutScore);
  let targetCal = Math.round(tdee + goalAdjustment);
  
  // Safety floors
  if (sex === 'female') {
    targetCal = Math.max(1200, targetCal);
  } else {
    targetCal = Math.max(1500, targetCal);
  }
  
  // ========================================
  // STEP 5: Calculate protein
  // ========================================
  const protein = calculateProtein(weightLbs, goal, goalWeightLbs, sex);
  
  // ========================================
  // STEP 6: Calculate fat (27.5% of calories)
  // ========================================
  const fatCal = targetCal * 0.275;
  let fat = Math.round(fatCal / 9);
  
  // ========================================
  // STEP 7: Calculate carbs (remainder)
  // ========================================
  let carbCal = targetCal - (protein * 4) - (fat * 9);
  let carbs = Math.round(carbCal / 4);
  
  // ========================================
  // STEP 8: Handle negative carbs
  // ========================================
  if (carbs < 0) {
    // Reduce fat to 25%
    const reducedFatCal = targetCal * 0.25;
    fat = Math.round(reducedFatCal / 9);
    carbCal = targetCal - (protein * 4) - (fat * 9);
    carbs = Math.round(carbCal / 4);
    
    if (carbs < 50) {
      carbs = 50;
    }
  }
  
  // ========================================
  // STEP 9: Final calories (4/4/9 rule)
  // ========================================
  const calories = (protein * 4) + (carbs * 4) + (fat * 9);
  
  return {
    target_calories: calories,
    target_protein_g: protein,
    target_carbs_g: carbs,
    target_fat_g: fat,
    calories,
    protein,
    carbs,
    fat,
    // Debug info
    _debug: {
      bmr: Math.round(bmr),
      baselineActivity,
      workoutScore,
      finalActivity,
      finalPal,
      tdee: Math.round(tdee),
      goalAdjustment,
      targetCalBeforeMacros: targetCal
    }
  };
}

// =========================================
// Component
// =========================================

const workoutOptions = [
  { id: 'strength_training', label: 'Strength Training' },
  { id: 'cardio', label: 'Cardio' },
  { id: 'hiit', label: 'HIIT' },
  { id: 'sports', label: 'Sports' },
  { id: 'fitness_classes', label: 'Fitness Classes' },
  { id: 'mobility', label: 'Mobility / Yoga' },
  { id: 'other', label: 'Other' },
];

const MacroQuiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setProfile, setOnboardingData, profile, onboardingData, user } = useUserStore();
  // profile?.* fallbacks cover back-navigation from ManualMacros
  // (MANUAL_MACROS_BACK_ROUTE only carries { mode: 'manual' }, not the
  // goal/goal-weight fields) — GoalSelection already wrote all three onto
  // the profile store before this screen was first reached, so re-reading
  // them from there on re-entry recovers exactly what location.state lost.
  // `??` (not `||`) matters for has_goal_weight specifically: `false` is a
  // legitimate, intentional value (the user answered "No" to the
  // goal-weight question) and must not be treated as missing.
  const goal = location.state?.goal || profile?.goal || 'Maintain';
  const goalWeightLbs = location.state?.goal_weight_lbs ?? profile?.goal_weight_lbs;
  const hasGoalWeight = location.state?.has_goal_weight ?? profile?.has_goal_weight;
  // Manual mode: this screen collects body/profile info ONLY — it must
  // never call backend target regeneration, never run the local V3
  // fallback, and must hand off to ManualMacros instead of Allergens.
  const isManualMode = isManualGoalMode(location.state);

  useKeyboardAware();

  // Derive auth state from user presence
  const isAuthenticated = !!user;
  const isDemoMode = user?.user_id?.startsWith('demo_');
  
  const restored = restoreMacroQuizState(profile, onboardingData, location.state);
  const [step, setStep] = useState(restored.step);
  const [loading, setLoading] = useState(false);
  const [unitSystem, setUnitSystem] = useState(restored.unitSystem);
  const [workouts, setWorkouts] = useState(restored.workouts);
  const [formData, setFormData] = useState(restored.formData);

  // Convert to metric for calculations
  const convertToMetric = () => {
    if (unitSystem === 'metric') {
      return {
        height_cm: parseFloat(formData.height),
        weight_kg: parseFloat(formData.weight)
      };
    } else {
      // Imperial to metric
      const heightCm = (parseFloat(formData.heightFeet || 0) * 30.48) + 
                       (parseFloat(formData.heightInches || 0) * 2.54);
      const weightKg = parseFloat(formData.weight) / 2.2046226218;
      return { height_cm: heightCm, weight_kg: weightKg };
    }
  };

  const handleComplete = async () => {
    setLoading(true);

    try {
      const { height_cm, weight_kg } = convertToMetric();

      const profileData = buildOnboardingProfileData({
        goal: goal,
        age: parseInt(formData.age),
        sex: formData.sex,
        units: unitSystem,
        height_cm: Math.round(height_cm),
        weight_kg: parseFloat(weight_kg.toFixed(1)),
        activity_level: formData.activity_level,
        workout_types: workouts,
        // Onboarding NEVER enables Auto Macros. buildOnboardingProfileData()
        // forces auto_macros_enabled:false for BOTH manual and automatic mode.
        // ManualMacros writes its own false when it saves the four targets;
        // automatic mode's successful regenerate-targets call is the SOLE
        // transition to auto_macros_enabled:true, written atomically with the
        // generated targets on the backend. A failed regeneration therefore
        // cannot leave the account marked Auto=true.
        goal_weight_lbs: goalWeightLbs || null,
        has_goal_weight: hasGoalWeight || false
      });

      if (isManualMode) {
        // Manual mode: collect and persist body/profile info ONLY. Never
        // call regenerateTargets(), never run the local V3 fallback, never
        // overwrite the targets the user is about to enter themselves in
        // ManualMacros.
        if (isAuthenticated && !isDemoMode) {
          try {
            await profileAPI.saveProfile(profileData);
          } catch (apiError) {
            console.error('[MacroQuiz] manual-mode profile save failed:', apiError);
            toast.error("Couldn't save your info. Please check your connection and try again.");
            return;
          }
          setProfile({ ...profile, ...profileData });
        } else {
          // Demo/local-only: store locally, no macro calculation of any kind.
          setOnboardingData(profileData);
          setProfile({ ...profile, ...profileData });
        }
      } else if (isAuthenticated && !isDemoMode) {
        let response;
        try {
          // Step 1: persist preliminary profile (auto_macros_enabled:false).
          // Step 2: generate macros on the backend — the SOLE transition to
          // auto_macros_enabled:true, atomic with the targets. If it fails,
          // the account stays Auto=false (no split-brain).
          response = await runAutomaticMacroSetup({
            profileData,
            saveProfile: profileAPI.saveProfile,
            regenerateTargets: profileAPI.regenerateTargets,
          });
        } catch (apiError) {
          // A real account's targets must come from the backend V4
          // calculator — never the local calculateMacros() fallback
          // (still V3) presented as equivalent. Remain on this screen so
          // the user can retry instead of silently continuing to
          // Allergens on invented/stale data (see AGENTS.md pre-launch
          // audit nutrition-readiness follow-up).
          console.error('[MacroQuiz] backend macro generation failed:', apiError);
          toast.error("Couldn't generate your targets. Please check your connection and try again.");
          return;
        }

        // Update local profile state with API response. Auto mode is now ON
        // (the backend flipped it atomically with the targets above).
        setProfile({
          ...profile,
          ...profileData,
          auto_macros_enabled: true,
          target_calories: response.data.target_calories,
          target_protein_g: response.data.target_protein_g,
          target_carbs_g: response.data.target_carbs_g,
          target_fat_g: response.data.target_fat_g,
          calories: response.data.calories,
          protein: response.data.protein,
          carbs: response.data.carbs,
          fat: response.data.fat,
        });
      } else {
        // Not logged in or demo mode - calculate locally
        calculateAndStoreMacrosLocally(profileData, height_cm, weight_kg);
      }

      const nextRoute = quizNextRoute(location.state, { goal, goal_weight_lbs: goalWeightLbs, has_goal_weight: hasGoalWeight });
      navigate(nextRoute.path, { state: nextRoute.state });
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateAndStoreMacrosLocally = (profileData, height_cm, weight_kg) => {
    // Use the unified calculation function
    const macros = calculateMacros({
      ...profileData,
      height_cm,
      weight_kg
    });
    
    const macroData = {
      ...profileData,
      ...macros
    };
    
    // Store locally for later sync after account creation
    setOnboardingData(macroData);
    setProfile({ ...profile, ...macroData });
  };

  return (
    <div
      className="min-h-screen header-safe-top px-6 pb-24"
      onClick={(e) => {
        // Tap outside any input to dismiss the iOS keyboard.
        if (
          e.target instanceof HTMLElement &&
          !e.target.closest('input, textarea, button, [role="button"], [data-testid]')
        ) {
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md mx-auto space-y-8"
      >
        {/* Progress indicator */}
        <div className="flex justify-center gap-2">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`w-2 h-2 rounded-full transition-colors ${
                s === step ? 'bg-accent' : s < step ? 'bg-accent/50' : 'bg-border'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="space-y-6"
          >
            <button
              onClick={() => navigate('/onboarding/goal', isManualMode ? { state: { mode: 'manual' } } : undefined)}
              data-testid="quiz-step1-back"
              className="flex items-center gap-1 text-muted-foreground"
            >
              <ChevronLeft size={18} /> Back
            </button>

            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto">
                <User size={24} className="text-accent" />
              </div>
              <h1 className="font-heading font-bold text-2xl">About You</h1>
              <p className="text-muted-foreground text-sm">Help us personalize your plan</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Age</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  enterKeyHint="next"
                  value={formData.age}
                  onChange={(e) => setFormData({...formData, age: e.target.value})}
                  placeholder="Enter your age"
                  data-testid="age-input"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Biological Sex</Label>
                <Select value={formData.sex} onValueChange={(value) => setFormData({...formData, sex: value})}>
                  <SelectTrigger data-testid="sex-select" className="mt-1">
                    <SelectValue placeholder="Select sex" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={() => setStep(2)}
              disabled={!formData.age || !formData.sex}
              data-testid="step1-next"
              className="w-full h-12 rounded-full"
            >
              Continue <ChevronRight size={18} className="ml-1" />
            </Button>
          </motion.div>
        )}

        {/* Step 2: Measurements */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="space-y-6"
          >
            <button onClick={() => setStep(1)} className="flex items-center gap-1 text-muted-foreground">
              <ChevronLeft size={18} /> Back
            </button>
            
            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto">
                <Ruler size={24} className="text-accent" />
              </div>
              <h1 className="font-heading font-bold text-2xl">Your Measurements</h1>
              <p className="text-muted-foreground text-sm">We'll use this to estimate your targets</p>
            </div>

            {/* Unit Toggle */}
            <div className="flex justify-center">
              <div className="inline-flex bg-secondary/60 rounded-full p-1">
                <button
                  onClick={() => setUnitSystem('imperial')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    unitSystem === 'imperial' ? 'bg-accent text-white' : 'text-muted-foreground'
                  }`}
                >
                  Imperial
                </button>
                <button
                  onClick={() => setUnitSystem('metric')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    unitSystem === 'metric' ? 'bg-accent text-white' : 'text-muted-foreground'
                  }`}
                >
                  Metric
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {unitSystem === 'metric' ? (
                <>
                  <div>
                    <Label>Height (cm)</Label>
                    <Input
                      type="number"
                      inputMode="decimal"
                      enterKeyHint="next"
                      value={formData.height}
                      onChange={(e) => setFormData({...formData, height: e.target.value})}
                      placeholder="e.g., 175"
                      data-testid="height-cm-input"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Weight (kg)</Label>
                    <Input
                      type="number"
                      inputMode="decimal"
                      enterKeyHint="done"
                      value={formData.weight}
                      onChange={(e) => setFormData({...formData, weight: e.target.value})}
                      placeholder="e.g., 70"
                      data-testid="weight-kg-input"
                      className="mt-1"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label>Height</Label>
                    <div className="flex gap-2 mt-1">
                      <div className="flex-1">
                        <Input
                          type="number"
                          inputMode="numeric"
                          enterKeyHint="next"
                          value={formData.heightFeet}
                          onChange={(e) => setFormData({...formData, heightFeet: e.target.value})}
                          placeholder="Feet"
                          data-testid="height-feet-input"
                        />
                      </div>
                      <div className="flex-1">
                        <Input
                          type="number"
                          inputMode="numeric"
                          enterKeyHint="next"
                          value={formData.heightInches}
                          onChange={(e) => setFormData({...formData, heightInches: e.target.value})}
                          placeholder="Inches"
                          data-testid="height-inches-input"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label>Weight (lbs)</Label>
                    <Input
                      type="number"
                      inputMode="decimal"
                      enterKeyHint="done"
                      value={formData.weight}
                      onChange={(e) => setFormData({...formData, weight: e.target.value})}
                      placeholder="e.g., 155"
                      data-testid="weight-lbs-input"
                      className="mt-1"
                    />
                  </div>
                </>
              )}
            </div>

            <Button
              onClick={() => setStep(3)}
              disabled={
                unitSystem === 'metric' 
                  ? !formData.height || !formData.weight
                  : !formData.heightFeet || !formData.weight
              }
              data-testid="step2-next"
              className="w-full h-12 rounded-full"
            >
              Continue <ChevronRight size={18} className="ml-1" />
            </Button>
          </motion.div>
        )}

        {/* Step 3: Activity Level */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="space-y-6"
          >
            <button onClick={() => setStep(2)} className="flex items-center gap-1 text-muted-foreground">
              <ChevronLeft size={18} /> Back
            </button>
            
            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto">
                <Activity size={24} className="text-accent" />
              </div>
              <h1 className="font-heading font-bold text-2xl">Activity Level</h1>
              <p className="text-muted-foreground text-sm">How active are you on a typical day?</p>
            </div>

            <div className="space-y-3">
              {[
                { value: 'sedentary', label: 'Sedentary', desc: 'Little to no exercise' },
                { value: 'light', label: 'Lightly Active', desc: 'Light exercise 1-3 days/week' },
                { value: 'moderate', label: 'Moderately Active', desc: 'Exercise 3-5 days/week' },
                { value: 'active', label: 'Very Active', desc: 'Hard exercise 6-7 days/week' },
                { value: 'very_active', label: 'Extremely Active', desc: 'Intense daily exercise or physical job' },
              ].map((level) => (
                <motion.div
                  key={level.value}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setFormData({...formData, activity_level: level.value})}
                  data-testid={`activity-${level.value}`}
                  className={`bg-card border rounded-xl p-4 cursor-pointer transition-all ${
                    formData.activity_level === level.value
                      ? 'border-accent bg-accent/5'
                      : 'border-border/40 hover:border-accent/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{level.label}</h3>
                      <p className="text-sm text-muted-foreground">{level.desc}</p>
                    </div>
                    {formData.activity_level === level.value && (
                      <div className="w-5 h-5 rounded-full bg-accent flex items-center justify-center">
                        <Check size={12} className="text-white" />
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <Button
              onClick={() => setStep(4)}
              disabled={!formData.activity_level}
              data-testid="step3-next"
              className="w-full h-12 rounded-full"
            >
              Continue <ChevronRight size={18} className="ml-1" />
            </Button>
          </motion.div>
        )}

        {/* Step 4: Workouts */}
        {step === 4 && (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="space-y-6"
          >
            <button onClick={() => setStep(3)} className="flex items-center gap-1 text-muted-foreground">
              <ChevronLeft size={18} /> Back
            </button>
            
            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto">
                <Dumbbell size={24} className="text-accent" />
              </div>
              <h1 className="font-heading font-bold text-2xl">Workouts You Do</h1>
              <p className="text-muted-foreground text-sm">Select all that apply (optional)</p>
            </div>

            <div className="space-y-3">
              {workoutOptions.map((workout) => (
                <motion.div
                  key={workout.id}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    if (workouts.includes(workout.id)) {
                      setWorkouts(workouts.filter(w => w !== workout.id));
                    } else {
                      setWorkouts([...workouts, workout.id]);
                    }
                  }}
                  data-testid={`workout-${workout.id.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`bg-card border rounded-xl p-4 cursor-pointer transition-all ${
                    workouts.includes(workout.id)
                      ? 'border-accent bg-accent/5'
                      : 'border-border/40 hover:border-accent/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{workout.label}</span>
                    {workouts.includes(workout.id) && (
                      <div className="w-5 h-5 rounded-full bg-accent flex items-center justify-center">
                        <Check size={12} className="text-white" />
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <Button
              onClick={handleComplete}
              disabled={loading}
              data-testid="complete-quiz-btn"
              className="w-full h-12 rounded-full"
            >
              {loading ? (isManualMode ? 'Saving...' : 'Calculating...') : 'Continue'}
            </Button>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default MacroQuiz;
